<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import { useNavigate } from "svelte-navigator";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { ROUTES } from "@utils/routes.js";
  import Header from "@components/Header.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import PhaseMessageLanguage from "../components/PhaseMessageLanguage.svelte";
  import {
    END_WHITELIST_MESSAGES,
    END_WHITELIST_PAUSE_MESSAGES,
  } from "./phaseMessages.js";
  import {
    InfoBlock,
    TextArea,
    Tooltip,
    SectionWrapper,
    Button
  } from "paxray-components-svelte";

  const { markStepAsCompleted } = projectListStore;
  const urlParams = useUrlParams();
  const navigate = useNavigate();
  const step = PROJECT_STAGES.SCOPING.steps.END_WHTLST;
  const stage = PROJECT_STAGES.SCOPING.id;

  let stepActionsRef;
  let openDangerModal = false;
  let actionPending = false;
  let actionError = "";
  let showWhitelistGuide = false;
  let whitelistGuideStep = 0;
  let whitelistGuideSteps = [];

  onMount(() => {
    const unregisterTour = registerViewTour(() => {
      whitelistGuideSteps = [
        {
          target: '[data-guide="end-whitelist-overview"]',
          title: "Finish collection",
          text: "You can end collection after at least seven days. Next, choose which apps and websites to include in your whitelist.",
        },
        {
          target:
            '[data-guide="end-whitelist-language"] .phase_message_language',
          title: "Choose a language",
          text: "Choose the language for both messages. Changing it resets any edits you have made.",
        },
        {
          target: '[data-guide="end-whitelist-message"]',
          title: "Check your message",
          text: "This is what participants see when collection ends. Use Edit message to make changes.",
        },
        {
          target: '[data-guide="end-whitelist-pause"]',
          title: "Check the pause message",
          text: "This message appears in the recorder while recording is paused. Leave it empty to use the default message.",
        },
        {
          target:
            '[data-guide="end-whitelist-action"] > button, [data-guide="end-whitelist-action"] > .completed-badge',
          title: "End collection",
          text: "Click End Whitelist Data Collection and confirm when you are ready. This ends collection and cannot be undone.",
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      whitelistGuideStep = 0;
      showWhitelistGuide = whitelistGuideSteps.length > 0;
    });
    return unregisterTour;
  });

  $: ({ idproject } = $urlParams);
  $: ({ currentProjectContacts } = $projectListStore);
  $: projectProgressHref = `${ROUTES.dam.progress}?${new URLSearchParams({ idproject }).toString()}`;

  let language = "en";
  let message = END_WHITELIST_MESSAGES.en;
  let pauseMessage = END_WHITELIST_PAUSE_MESSAGES.en;
  let editingMessage = false;

  async function endWhitelistPhase() {
    if (actionPending) return;
    actionPending = true;
    actionError = "";
    try {
      const response = await fetchWithSession(
        "/api/damprojects/endScopeRecording",
        {
          method: "POST",
          body: JSON.stringify({ idproject, message, pauseMessage }),
        },
      );
      if (!response.ok)
        throw new Error("Could not end whitelist data collection");
      const data = await response.json();
      if (!data?.ok) throw new Error("Could not end whitelist data collection");
      const completion = await markStepAsCompleted(idproject, step, stage);
      if (completion?.ok) {
        stepActionsRef?.refresh();
        navigate(`${ROUTES.dam.progress}?idproject=${idproject}`);
      } else {
        actionError =
          "Collection ended, but the project status could not be updated. Reload this page to check the current status.";
      }
    } catch (error) {
      console.error("Error ending whitelist data collection:", error);
      actionError =
        "Data collection could not be ended. Reload this page to check the current status.";
    } finally {
      actionPending = false;
      openDangerModal = false;
    }
  }
</script>

<Header
  section="Project"
  area="Scoping"
  breadcrumb="End Whitelist Data Collection"
  areaHref={`${projectProgressHref}#scoping-end`}
  tooltipText="Stop whitelist data collection and show participants the configured messages. Afterwards, define the whitelist. Ending cannot be undone."
>
  <div data-guide="end-whitelist-action">
    <ProjectStepActions
      bind:this={stepActionsRef}
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      text="End Whitelist Data Collection"
      disabled={actionPending}
      allowAdminOverride={false}
      confirmBeforeComplete={false}
      onClick={() => (openDangerModal = true)}
    />
  </div>
</Header>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main">
    {#if actionError}
      <InfoBlock
        type="warning"
        heading="Check collection status"
        message={actionError}
        showClose={false}
      />
    {/if}
    <div data-guide="end-whitelist-overview">
      <InfoBlock
        type="info"
        heading="End Whitelist Data Collection"
        message="Review the participant and pause messages, then end data collection. This is possible after at least seven days."
        showClose={false}
      />
    </div>
    <div class="row">
      <div class="cell">
        <div class="field_label" data-guide="end-whitelist-language">
          <PhaseMessageLanguage
            messages={END_WHITELIST_MESSAGES}
            consents={END_WHITELIST_PAUSE_MESSAGES}
            bind:language
            bind:value={message}
            bind:consent={pauseMessage}
          />
          <Button
            size="field"
            kind="secondary"
            on:click={() => (editingMessage = !editingMessage)}
          >
            {editingMessage ? "Done editing" : "Edit message"}
          </Button>
        </div>
        <div data-guide="end-whitelist-message">
          {#if editingMessage}
            <TextArea
              placeholder="Enter a message for the users"
              bind:value={message}
              richText
              persons={currentProjectContacts}
            />
          {:else}
            <div class="message-preview">
              {@html message}
            </div>
          {/if}
        </div>
        <div class="pause_message" data-guide="end-whitelist-pause">
          {#if editingMessage}
            <TextArea
              id="whitelist-pause-message"
              labelText="Pause Message"
              hideLabel
              placeholder="Enter the text shown while recording is paused"
              rows={3}
              bind:value={pauseMessage}
              tooltipText="Plain text shown in the recorder menu while recording is paused. Leave empty for the recorder's default text."
            />
          {:else}
            <div class="field_label">
              <label class="bx--label" for="whitelist-pause-message"
                >Pause Message</label
              >
              <Tooltip
                text="Plain text shown in the recorder menu while recording is paused. Leave empty for the recorder's default text."
              />
            </div>
            <p class="pause-preview">
              {pauseMessage || "The recorder's default text will be shown."}
            </p>
          {/if}
        </div>
      </div>
    </div>
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showWhitelistGuide}
  bind:currentStep={whitelistGuideStep}
  steps={whitelistGuideSteps}
  allowInteraction={false}
/>

<DangerModal
  danger
  bind:open={openDangerModal}
  preventCloseOnClickOutside
  modalHeading="End Whitelist Data Collection?"
  primaryButtonText="End Whitelist Data Collection"
  primaryButtonDisabled={actionPending}
  secondaryButtonText="Cancel"
  on:click:button--primary={endWhitelistPhase}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <p>
    Stop data collection and show the configured participant and pause
    messages. Next, define the whitelist. This action cannot be undone.
  </p>
</DangerModal>

<style>
  .field_label {
    display: flex;
    align-items: flex-end;
    gap: var(--xs);
    margin-bottom: var(--control-gap);
  }

  .field_label .bx--label {
    margin: 0;
  }

  .field_label :global(.phase_message_language) {
    margin-left: 0;
  }

  .message-preview,
  .pause-preview {
    padding: 1rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    overflow-wrap: anywhere;
  }

  .pause-preview {
    margin: 0;
    white-space: pre-wrap;
  }

  .main {
    max-width: 100%;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .row {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
  }

  .cell {
    flex: 1 1 auto;
    width: 50%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .pause_message {
    flex: 0 0 auto;
    margin-top: 1rem;
  }

  .pause_message :global(.bx--text-area) {
    background: transparent;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
  }

  .message-preview :global(b),
  .message-preview :global(strong) {
    font-weight: bold;
  }

  .message-preview :global(i),
  .message-preview :global(em) {
    font-style: italic;
  }

  .message-preview :global(u) {
    text-decoration: underline;
  }
</style>
